<template>
  <surg-approve
    v-if="surgery && documents"
    :surgery="surgery"
    :files="documents"
    :patientRequests="patientRequests"
    :isInfo="true"
    @approveGoBack="$router.go(-1)"
  />
</template>

<script>
import { mapMutations } from "vuex";
import {
  emptySurgeryDocs,
  getSurgeryDocuments
} from "../../../utilities/download-document";
import {
  createEmptyPatientRequests,
  parsePatientRequests
} from "@/utilities/patient-requests";
import SurgApprove from "./SurgerySummary/SurgApprove.vue";

export default {
  components: { SurgApprove },
  name: "SurgInfo",
  data() {
    return {
      surgery: null,
      documents: emptySurgeryDocs,
      patientRequests: createEmptyPatientRequests()
    };
  },
  methods: {
    ...mapMutations("Surgery", ["SET_IMPLANTS_LIST"]),

    async getDocuments() {
      this.documents = await getSurgeryDocuments(this.$route.params.id);
    },

    /**
     * Parses gender from API response
     */
    parseGender(gender) {
      if (gender === "male" || gender === "זכר") {
        return "male";
      } else if (gender === "female" || gender === "נקבה") {
        return "female";
      }
      return "";
    },

    /**
     * Parses surgery orders for display
     */
    parseSurgeryOrders(resOrders) {
      const current_surgery = [];
      const insurance_surgery = [];

      if (resOrders?.status === "OK" && resOrders.data?.orders) {
        resOrders.data.orders.forEach(s => {
          if (s.tafnit_code === "9000") return;

          const duration = s.duration
            ? this.$moment.utc(s.duration * 1000).format("HH:mm")
            : "HH:mm";

          current_surgery.push({
            is_primary: s.is_primary || "",
            procedure: s.icd9_procedure,
            surgery_name: s.type,
            surgery_duration: duration,
            default_surgery_duration: duration,
            side: s.side ?? ""
          });

          let insurance = s.insurance_name;
          if (
            s.tafnit_code == 1 &&
            insurance !== "כללית מושלם" &&
            insurance !== "פרטי"
          ) {
            insurance = "חברת ביטוח";
          }

          insurance_surgery.push({
            insurance,
            surgery_name: s.insurance_procedure,
            notice: s.notes
          });
        });
      }

      return { current_surgery, insurance_surgery };
    },

    /**
     * Parses diagnosis data from surgery
     */
    parseDiagnosis(diagnosisJson) {
      const current_diagnosis = [];
      const past_diagnosis = [];

      if (diagnosisJson) {
        const diagnosis = JSON.parse(diagnosisJson);

        if (diagnosis.current?.length) {
          diagnosis.current.forEach(d => {
            current_diagnosis.push({
              side: d.side ?? "",
              diagnosis_number: d.diagnosis_number,
              diagnosis_name: d.diagnosis_name,
              notice: d.notice
            });
          });
        }

        if (diagnosis.past?.length) {
          diagnosis.past.forEach(d => {
            past_diagnosis.push({
              side: d.side ?? "",
              diagnosis_number: d.diagnosis_number,
              diagnosis_name: d.diagnosis_name,
              notice: d.notice
            });
          });
        }
      }

      return { current_diagnosis, past_diagnosis };
    },

    /**
     * Parses sensitivity data from patient
     */
    parseSensitivity(sensitivityJson) {
      const medication_sensitivity = [];
      const material_sensitivity = [];

      if (sensitivityJson) {
        const sensitivity = JSON.parse(sensitivityJson);

        if (sensitivity.medication?.length) {
          sensitivity.medication.forEach(sens => {
            medication_sensitivity.push({
              response: sens.response,
              severity: sens.severity,
              medication: sens.medication,
              notice: sens.notice
            });
          });
        }

        if (sensitivity.material?.length) {
          sensitivity.material.forEach(material => {
            material_sensitivity.push({
              response: material.response,
              severity: material.severity,
              material_name: material.material_name,
              notice: material.notice
            });
          });
        }
      }

      return { medication_sensitivity, material_sensitivity };
    }
  },

  async mounted() {
    this.$root.$on("getSurgeryInfo", () => {
      this.getDocuments();
    });

    const surgeryId = this.$route.params.id;
    const [res, resOrders] = await Promise.all([
      this.$API.getSurgeryInfo(surgeryId),
      this.$API.getSurgeryOrders(surgeryId)
    ]);

    if (res?.status !== "OK") return;

    const surg = res.message.surgery;
    const patient = res.message.patient;

    // Build surgery object for display
    this.surgery = { ...surg };

    // Patient data
    this.surgery.first_name = patient.firstName;
    this.surgery.last_name = patient.lastName;
    this.surgery.passport = patient.passport;
    this.surgery.birth_date = patient.birthDate;
    this.surgery.gender = this.parseGender(patient.gender);
    this.surgery.phone = patient.phone;
    this.surgery.second_phone = patient.phone2;
    this.surgery.apotropos = patient.apotropos;
    this.surgery.parental_status = res.message.surgery.parental_status;
    this.surgery.email = patient.email;
    this.surgery.user_language = patient.language;

    if (patient.passport_type) {
      this.surgery.passport_type = patient.passport_type;
    }

    // Surgery data
    this.surgery.start_date = res.message.surgery.start_date;
    this.surgery.insurance_fee = res.message.surgery.insurance_fee;
    this.surgery.handle_insurance_claim_by_hmc =
      res.message.surgery.handle_insurance_claim_by_hmc;
    this.surgery.pre_surgery_override =
      res.message.surgery.preSurgery_status === "pre_needed";

    // Implants - update store for SurgApprove to use.
    // Backend returns implants as [{ id, supplier_name, name }]; map to ids so
    // SurgApprove can resolve names against implantsList.
    const implantsList = res.message.surgery.implants || [];
    this.SET_IMPLANTS_LIST(implantsList);
    this.surgery.implants = implantsList.map(implant => implant.id);

    // Custom implant
    if (res.message.surgery.custom_implant) {
      this.surgery.custom_implant = res.message.surgery.custom_implant;
    }

    // Insurance
    this.surgery.insurance_status_heb =
      res.message.surgery.insurance_status_heb;
    this.surgery.insurance_code = res.message.surgery.insurance_code;

    // Pre-surgery appointments
    this.surgery.pre_surgery_appointments =
      res.message.pre_surgery_appointments;

    this.patientRequests = parsePatientRequests(res, resOrders);

    // Parse orders
    const { current_surgery, insurance_surgery } = this.parseSurgeryOrders(
      resOrders
    );
    this.surgery.current_surgery = current_surgery;
    this.surgery.insurance_surgery = insurance_surgery;

    // Parse diagnosis
    const { current_diagnosis, past_diagnosis } = this.parseDiagnosis(
      surg.diagnosis
    );
    this.surgery.current_diagnosis = current_diagnosis;
    this.surgery.past_diagnosis = past_diagnosis;

    // Physical examination
    this.surgery.physical_examination = surg.physicalExam;

    // Parse sensitivity
    const {
      medication_sensitivity,
      material_sensitivity
    } = this.parseSensitivity(patient.sensitivity);
    this.surgery.medication_sensitivity = medication_sensitivity;
    this.surgery.material_sensitivity = material_sensitivity;

    // RHINOPLASTY special case - important business logic
    if (
      this.surgery.current_surgery.length === 1 &&
      this.surgery.current_surgery[0].surgery_name === "1 > RHINOPLASTY" &&
      this.surgery.secondary_insurance_name === "כללית מושלם"
    ) {
      this.surgery.insurance_name = "כללית מושלם";
    }

    await this.getDocuments();
  }
};
</script>
